import { makeAutoObservable, runInAction } from 'mobx';
import { notification } from 'common/utils/notification';
import configurationStore from 'common/stores/configuration/configuration.store';
import addressesService from 'common/services/addresses-service/addresses.service';
import { IStateRead } from 'common/services/addresses-service/addresses.service.types';
import payEaseService from '../services/payease.service';
import { ICreateTransactionRequest } from '../services/payease.service.types';
import { passFromPrice, passHasOptions } from '../payease.utils';
import { ICheckoutForm, IPass, IPassOption, PassCategory, PaymentMethod } from '../payease.types';

const EMPTY_FORM: ICheckoutForm = {
  firstName: '',
  lastName: '',
  company: '',
  email: '',
  licensePlate: '',
  state: '',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ICompletedSale {
  method: PaymentMethod;
}

class PayEaseStore {
  loading = true;
  passes: IPass[] = [];
  states: IStateRead[] = [];

  selectedPassId: string | null = null;
  selectedOptionId: string | null = null;
  optionPickerPassId: string | null = null;

  form: ICheckoutForm = { ...EMPTY_FORM };

  submittingMethod: PaymentMethod | null = null;
  completedSale: ICompletedSale | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  async init() {
    this.loading = true;
    this.completedSale = null;
    this.resetAfterSale();

    try {
      const [passes, states] = await Promise.all([payEaseService.getPasses(), addressesService.getStates()]);
      runInAction(() => {
        this.passes = passes;
        this.states = states;
        this.form.state = this.defaultState;
      });
    } catch (error) {
      notification.error({ message: this.extractError(error, 'Failed to load PayEase. Please try again.') });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  get sortedPasses(): IPass[] {
    return [...this.passes].sort((a, b) => passFromPrice(a) - passFromPrice(b));
  }

  get contractorPasses(): IPass[] {
    return this.sortedPasses.filter((pass) => pass.category === PassCategory.Contractor);
  }

  get residentAndMemberPasses(): IPass[] {
    return this.sortedPasses.filter((pass) => pass.category === PassCategory.Resident || pass.category === PassCategory.Member);
  }

  get stateOptions(): { value: string; label: string }[] {
    return this.states.map((state) => ({ value: state.abbreviation, label: `${state.abbreviation} — ${state.name}` }));
  }

  get defaultState(): string {
    const configured = configurationStore.configuration?.default.defaultLicenseState;
    if (!configured) {
      return '';
    }
    const match = this.states.find((state) => state.name === configured || state.abbreviation === configured);
    return match?.abbreviation ?? '';
  }

  get selectedPass(): IPass | null {
    return this.passes.find((pass) => pass.id === this.selectedPassId) ?? null;
  }

  get selectedOption(): IPassOption | null {
    if (!this.selectedPass) {
      return null;
    }
    return this.selectedPass.options.find((option) => option.id === this.selectedOptionId) ?? null;
  }

  get pickerPass(): IPass | null {
    return this.passes.find((pass) => pass.id === this.optionPickerPassId) ?? null;
  }

  get effectiveAmount(): number {
    return this.selectedOption?.price ?? 0;
  }

  get normalizedPlate(): string {
    return this.form.licensePlate.trim().toUpperCase();
  }

  get hasBuyerIdentity(): boolean {
    const hasName = this.form.firstName.trim().length > 0 && this.form.lastName.trim().length > 0;
    return hasName || this.form.company.trim().length > 0;
  }

  get isEmailValid(): boolean {
    const email = this.form.email.trim();
    return email.length === 0 || EMAIL_PATTERN.test(email);
  }

  get validationHint(): string | null {
    if (!this.selectedOption) {
      return 'Select a pass to continue.';
    }
    if (this.normalizedPlate.length === 0) {
      return 'Enter the license plate.';
    }
    if (this.form.state.length === 0) {
      return 'Select the state.';
    }
    if (!this.hasBuyerIdentity) {
      return 'Enter a driver name (first and last) or a company.';
    }
    if (!this.isEmailValid) {
      return 'Enter a valid email or leave it blank.';
    }
    return null;
  }

  get submitting(): boolean {
    return this.submittingMethod !== null;
  }

  get isReadyToSubmit(): boolean {
    return this.validationHint === null && !this.submitting && !this.completedSale;
  }

  handlePassTap(pass: IPass) {
    if (this.completedSale) {
      return;
    }
    if (passHasOptions(pass)) {
      this.openOptionPicker(pass.id);
    } else {
      this.selectedPassId = pass.id;
      this.selectedOptionId = pass.options[0]?.id ?? null;
    }
  }

  handleOptionPick(optionId: string) {
    if (!this.optionPickerPassId) {
      return;
    }
    this.selectedPassId = this.optionPickerPassId;
    this.selectedOptionId = optionId;
    this.optionPickerPassId = null;
  }

  openOptionPicker(passId: string) {
    this.optionPickerPassId = passId;
  }

  closeOptionPicker() {
    this.optionPickerPassId = null;
  }

  setFormField(field: keyof ICheckoutForm, value: string) {
    this.form[field] = value;
  }

  resetAfterSale() {
    this.form = { ...EMPTY_FORM, state: this.defaultState };
    this.selectedPassId = null;
    this.selectedOptionId = null;
  }

  startNewSale() {
    this.completedSale = null;
    this.resetAfterSale();
  }

  async submit(paymentMethod: PaymentMethod) {
    const pass = this.selectedPass;
    const option = this.selectedOption;

    if (!pass || !option || !this.isReadyToSubmit) {
      return;
    }

    this.submittingMethod = paymentMethod;

    try {
      await payEaseService.createTransaction(pass.id, option.id, this.buildTransactionRequest(paymentMethod));

      runInAction(() => {
        this.completedSale = { method: paymentMethod };
      });

      this.notifySuccess(paymentMethod);
    } catch (error) {
      notification.error({ message: this.extractError(error, 'Payment failed. Please try again.') });
    } finally {
      runInAction(() => {
        this.submittingMethod = null;
      });
    }
  }

  private notifySuccess(paymentMethod: PaymentMethod) {
    if (paymentMethod === PaymentMethod.Cash) {
      notification.success({ message: 'Cash payment recorded, transaction completed.' });
      return;
    }

    notification.success({ message: 'Payment initiated, routed to the terminal.' });
  }

  private extractError(error: unknown, fallback: string): string {
    const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
    return message ?? fallback;
  }

  private buildTransactionRequest(paymentMethod: PaymentMethod): ICreateTransactionRequest {
    return {
      licensePlate: this.normalizedPlate,
      state: this.form.state,
      firstName: this.form.firstName.trim() || null,
      lastName: this.form.lastName.trim() || null,
      company: this.form.company.trim() || null,
      email: this.form.email.trim() || null,
      paymentMethod,
    };
  }
}

const payeaseStore = new PayEaseStore();

export default payeaseStore;
